import { useMemo } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Filler,
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

export interface HighlightsTrendLineChartProps {
    rows: { label: string; value: number }[];
    datasetLabel: string;
    /** Default 288 (~h-72). */
    heightPx?: number;
}

export function HighlightsTrendLineChart({
    rows,
    datasetLabel,
    heightPx = 288,
}: HighlightsTrendLineChartProps) {
    const data = useMemo(
        () => ({
            labels: rows.map((r) => r.label),
            datasets: [
                {
                    label: datasetLabel,
                    data: rows.map((r) => r.value),
                    borderColor: "#EC6E51",
                    backgroundColor: "rgba(236, 110, 81, 0.06)",
                    borderWidth: 2,
                    fill: true,
                    tension: 0.35,
                    pointRadius: rows.length > 20 ? 0 : 3,
                    pointHoverRadius: rows.length > 20 ? 0 : 4,
                    pointHitRadius: rows.length > 20 ? 4 : 8,
                    spanGaps: true,
                },
            ],
        }),
        [rows, datasetLabel]
    );

    const options = useMemo<ChartOptions<"line">>(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            interaction: { intersect: false, mode: "index" },
            plugins: {
                legend: { display: false },
                tooltip: {
                    animation: false,
                    callbacks: {
                        label: (ctx) => {
                            const y = ctx.parsed.y;
                            const n = typeof y === "number" ? y : 0;
                            return `${datasetLabel}: ${n.toLocaleString("en-IN")}`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    grid: { color: "rgba(0,0,0,0.06)" },
                    ticks: {
                        maxRotation: rows.length > 10 ? 40 : 0,
                        minRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 16,
                        font: { size: 10 },
                    },
                },
                y: {
                    beginAtZero: true,
                    grid: { color: "rgba(0,0,0,0.06)" },
                    ticks: {
                        precision: 0,
                        font: { size: 10 },
                    },
                },
            },
        }),
        [rows.length, datasetLabel]
    );

    return (
        <div className="relative w-full min-w-0" style={{ height: heightPx }}>
            <Line data={data} options={options} />
        </div>
    );
}
